import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Clock, Play, Square, Send, CheckCircle, Loader2, Calendar, Globe } from "lucide-react";
import { 
  formatSydney, 
  formatTimeSydney, 
  formatTableDateSydney, 
  toSydneyTime,
  getCurrentSydneyDisplay 
} from "../../utils/timezone";

const statusConfig = {
  draft: { label: "Draft", className: "badge-draft" },
  submitted: { label: "Submitted", className: "badge-submitted" },
  approved: { label: "Approved", className: "badge-approved" },
  rejected: { label: "Rejected", className: "badge-rejected" },
};

export default function StaffTimesheet() {
  const { api } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [clockOutDialog, setClockOutDialog] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(getCurrentSydneyDisplay());

  const fetchData = async () => {
    try {
      const [entriesRes, todayRes, timeRes] = await Promise.all([
        api().get("/timesheet"),
        api().get("/timesheet/today"),
        api().get("/server-time"),
      ]);
      setTimesheets(entriesRes.data);
      setTodayData(todayRes.data);
      setServerTime(timeRes.data);
    } catch (error) {
      toast.error("Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Update time display every second
    const interval = setInterval(() => {
      setCurrentTime(getCurrentSydneyDisplay());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      await api().post("/timesheet/clock-in", {});
      toast.success("Clocked in successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock in");
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      await api().post("/timesheet/clock-out", { break_minutes: parseInt(breakMinutes) || 0 });
      toast.success("Clocked out successfully!");
      setClockOutDialog(false);
      setBreakMinutes(0);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock out");
    } finally {
      setClockingOut(false);
    }
  };

  const handleSubmitMonth = async () => {
    const draftEntries = timesheets.filter(t => t.status === "draft" && t.clock_out);

    if (draftEntries.length === 0) {
      toast.error("No draft timesheets to submit");
      return;
    }

    setSubmitting(true);
    try {
      for (const entry of draftEntries) {
        await api().post(`/timesheet/${entry.id}/submit`);
      }
      toast.success(`${draftEntries.length} timesheet(s) submitted for approval!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const isClockedIn = todayData?.active_session != null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  // Use server date for calculations
  const serverDate = serverTime?.date || formatSydney(new Date(), "yyyy-MM-dd");
  
  // Calculate monthly stats using server date
  const currentMonth = serverDate.substring(0, 7); // YYYY-MM
  const currentMonthEntries = timesheets.filter(t => t.date?.startsWith(currentMonth));
  
  const monthlyHours = currentMonthEntries.reduce((sum, t) => sum + (t.total_hours || 0), 0);
  const monthlyDays = [...new Set(currentMonthEntries.filter(t => t.clock_out).map(t => t.date))].length;
  const draftCount = currentMonthEntries.filter(t => t.status === "draft" && t.clock_out).length;
  const approvedCount = currentMonthEntries.filter(t => t.status === "approved").length;

  // Weekly stats - last 7 days from server date
  const weekHours = timesheets
    .filter(t => {
      if (!t.date) return false;
      const entryDate = new Date(t.date);
      const serverDateObj = new Date(serverDate);
      const weekAgo = new Date(serverDateObj);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return entryDate >= weekAgo && entryDate <= serverDateObj;
    })
    .reduce((sum, t) => sum + (t.total_hours || 0), 0);

  // Today's entries
  const todayEntries = todayData?.entries || [];
  const todayTotalHours = todayData?.total_hours_today || 0;

  // Month name for display
  const monthName = formatSydney(serverDate, "MMMM yyyy");

  return (
    <div className="space-y-8" data-testid="staff-timesheet">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Timesheet</h1>
          <p className="text-slate-500 mt-1">{currentTime}</p>
          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
            <Globe className="h-3 w-3" />
            Sydney Time (AEDT) - Server Date: {serverTime?.date || "Loading..."}
          </p>
        </div>
        
        <div className="flex gap-3">
          {isClockedIn ? (
            <Button
              onClick={() => setClockOutDialog(true)}
              className="bg-red-500 hover:bg-red-600 btn-active"
              data-testid="clock-out-btn"
            >
              <Square className="mr-2 h-4 w-4" />
              Clock Out
            </Button>
          ) : (
            <Button
              onClick={handleClockIn}
              disabled={clockingIn}
              className="bg-emerald-500 hover:bg-emerald-600 btn-active"
              data-testid="clock-in-btn"
            >
              <Play className="mr-2 h-4 w-4" />
              {clockingIn ? "Clocking in..." : "Clock In"}
            </Button>
          )}
        </div>
      </div>

      {/* Active Session Card */}
      {isClockedIn && todayData?.active_session && (
        <Card className="border-emerald-200 bg-emerald-50 animate-fade-in">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse-soft" />
              <div>
                <p className="font-semibold text-emerald-800">Currently Working</p>
                <p className="text-sm text-emerald-600">
                  Clocked in at {formatTimeSydney(todayData.active_session.clock_in)} on {formatSydney(todayData.active_session.date, "dd MMM")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Sessions */}
      {todayEntries.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
              <Clock className="h-5 w-5" />
              Today's Sessions ({todayEntries.length}) - {serverTime?.date}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {todayEntries.map((entry, index) => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-blue-600">Session {index + 1}</span>
                    <span className="text-sm text-slate-600">
                      {formatTimeSydney(entry.clock_in)} 
                      {entry.clock_out ? ` - ${formatTimeSydney(entry.clock_out)}` : " - In Progress"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {entry.clock_out ? (
                      <span className="font-mono text-sm font-medium">{entry.total_hours?.toFixed(2)}h</span>
                    ) : (
                      <Badge className="badge-pending">Active</Badge>
                    )}
                  </div>
                </div>
              ))}
              <div className="pt-2 mt-2 border-t border-blue-200 flex justify-between items-center">
                <span className="text-sm font-medium text-blue-800">Today's Total (completed)</span>
                <span className="font-mono text-lg font-bold text-blue-600">{todayTotalHours.toFixed(2)}h</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Summary Card */}
      <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-slate-600" />
            {monthName} Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-3xl font-bold text-blue-600 font-mono">{monthlyHours.toFixed(1)}h</p>
              <p className="text-sm text-slate-500">Total Hours</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-3xl font-bold text-slate-700 font-mono">{monthlyDays}</p>
              <p className="text-sm text-slate-500">Days Worked</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-3xl font-bold text-amber-600 font-mono">{draftCount}</p>
              <p className="text-sm text-slate-500">Pending Submit</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
              <p className="text-3xl font-bold text-emerald-600 font-mono">{approvedCount}</p>
              <p className="text-sm text-slate-500">Approved</p>
            </div>
          </div>
          
          {/* Submit Button - Always available */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <Button
              onClick={handleSubmitMonth}
              disabled={submitting || draftCount === 0}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="submit-month-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Timesheets ({draftCount} entries)
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{weekHours.toFixed(1)}h</p>
                <p className="text-sm text-slate-500">This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {timesheets.filter(t => t.status === "approved").length}
                </p>
                <p className="text-sm text-slate-500">Total Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 rounded-lg">
                <Send className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {timesheets.filter(t => t.status === "submitted").length}
                </p>
                <p className="text-sm text-slate-500">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timesheet List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">All Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Break</th>
                  <th>Total Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-500">
                      No timesheet entries yet
                    </td>
                  </tr>
                ) : (
                  timesheets.map((entry) => {
                    const status = statusConfig[entry.status];
                    return (
                      <tr key={entry.id} data-testid={`timesheet-row-${entry.id}`}>
                        <td className="font-medium">
                          {formatTableDateSydney(entry.date)}
                        </td>
                        <td>{formatTimeSydney(entry.clock_in)}</td>
                        <td>{entry.clock_out ? formatTimeSydney(entry.clock_out) : "-"}</td>
                        <td>{entry.break_minutes}m</td>
                        <td className="font-mono font-medium">
                          {entry.total_hours?.toFixed(2) || "0.00"}h
                        </td>
                        <td>
                          <Badge className={status.className}>{status.label}</Badge>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Clock Out Dialog */}
      <Dialog open={clockOutDialog} onOpenChange={setClockOutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Clock Out</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="break">Break Minutes (optional)</Label>
              <Input
                id="break"
                type="number"
                min="0"
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(e.target.value)}
                placeholder="e.g., 30"
                data-testid="break-minutes-input"
              />
              <p className="text-xs text-slate-500">
                Break time will be subtracted from total hours
              </p>
            </div>
            <Button
              onClick={handleClockOut}
              disabled={clockingOut}
              className="w-full bg-red-500 hover:bg-red-600"
              data-testid="confirm-clock-out-btn"
            >
              {clockingOut ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Clocking out...
                </>
              ) : (
                <>
                  <Square className="mr-2 h-4 w-4" />
                  Confirm Clock Out
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
